import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy'
import { Select } from '@swaggerhub-portal/component-library'
import { RoutingSettingsButton } from 'app/components/ApiViewer/RoutingSettingsButton'
import { EditContentButtons } from 'app/components/ConsumerEditContentButtons'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'
import { useMediaQuery } from 'usehooks-ts'

import { ExploreButton } from './ExploreButton'
import { OpenAPIRendererToggle } from './OpenAPIRendererToggle'
import appDefaults from '~/app.defaults.json'
import AsyncApiRenderer from '~/components/ApiViewer/AsyncApiRenderer'
import { GenerateCodeDropdown } from '~/components/ApiViewer/GenerateCodeDropdown'
import SwaggerRenderer from '~/components/ApiViewer/SwaggerRenderer'
import { BreadcrumbBar } from '~/components/ConsumerBreadcrumbBar'
import LastModifiedDate from '~/components/LastModifiedDate'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { APIRenderers, OpenAPIRendererState, RoutingSettings, SHUBApiDetails } from '~/types/api'
import { AsyncAPIDefinition, OpenAPI2Definition, OpenAPI3Definition } from '~/types/api.definitions'
import { GeneratorLanguages } from '~/types/code.generator'
import { SelectItem } from '~/types/ui.components'
import { API_FILE_TYPES, downloadApi } from '~/utils/shared/downloadApi'

const apiDownloadOptions: { id: string, name: string }[] = [
  {
    id: '1',
    name: API_FILE_TYPES.JSON
  },
  {
    id: '2',
    name: API_FILE_TYPES.YAML
  }
]

const ElementsRenderer = reactLazyWithRetry(() => import('./ElementsRenderer').then(comp => ({ default: comp.ElementsRenderer } as any)))

export default function ApiViewer({
  title,
  definition,
  generatorLanguages,
  productId,
  path,
  exploreUrl,
  shubUrl,
  SHUBApiDetails,
  showEditButtons = false,
  renderer,
  lastModifiedDate,
  accentColor,
  routingSettings,
  providerContentSlug,
  isConsumerSide,
}: {
  title?: string;
  definition: OpenAPI2Definition | OpenAPI3Definition | AsyncAPIDefinition;
  generatorLanguages?: GeneratorLanguages,
  productId?: string,
  path?: string,
  exploreUrl?: string,
  shubUrl?: string,
  SHUBApiDetails: SHUBApiDetails,
  showEditButtons?: boolean,
  renderer?: APIRenderers | OpenAPIRendererState,
  lastModifiedDate?: string,
  accentColor?: string,
  routingSettings?: RoutingSettings,
  providerContentSlug?: string,
  isConsumerSide?: boolean,
}) {
  const { t } = useTranslation()
  const { hash, pathname } = useLocation()

  const isHydrated = useHydrated()

  const isAdmin = useMemo(() => pathname?.toLowerCase()?.includes('/sp-admin/'), [pathname])
  const [ref, setRef] = useState<HTMLDivElement>()

  const containerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setRef(node)
    }
  }, [])

  const onDownloadApi = (type: API_FILE_TYPES) => {
    downloadApi(type, definition, title || 'api')
  }

  const oasVersion = definition.openapi || definition.swagger || ''

  const isOas3_1 = oasVersion.startsWith('3.1')
  const isOas3 = oasVersion.startsWith('3.0')

  const isAsync = renderer === APIRenderers.ASYNCAPI

  const isMobileScreen = useMediaQuery(appDefaults.media.mobile, { initializeWithValue: false })
  const isMobile = isConsumerSide && isMobileScreen

  const shouldRenderRouting = !isAsync
  const shouldRenderDownload = title && (isAsync || isOas3_1)
  const shouldRenderCodeGen = title && (!isAsync && !isOas3_1) && generatorLanguages
  const shouldRenderOpenApiRendererToggle = renderer === OpenAPIRendererState.TOGGLE && isHydrated

  const shouldRenderToolbarDesktop = !isMobile && (shouldRenderRouting || shouldRenderDownload || shouldRenderCodeGen || isOas3 || showEditButtons)
  const shouldRenderToolbarMobile = isMobile && shouldRenderOpenApiRendererToggle
  const shouldRenderToolbar = shouldRenderToolbarDesktop || shouldRenderToolbarMobile

  const rendererRef = useRef<HTMLDivElement>(null)

  const [localRenderer, setLocalRenderer] = useLocalStorage<APIRenderers>('portal.apiViewer.renderer', APIRenderers.ELEMENTS)

  useEffect(() => {
    if (rendererRef.current && renderer === APIRenderers.ELEMENTS) {
      rendererRef.current.scrollTo(0, 0)
    }
  }, [hash])

  const renderApi = useCallback(() => {
    // We need to render Elements on UI side because it's loads some styles on the fly
    if (!ref) {
      return
    }
    const activeRenderer = renderer === OpenAPIRendererState.TOGGLE ? localRenderer : renderer
    if (activeRenderer === APIRenderers.ASYNCAPI) {
      return <AsyncApiRenderer definition={definition as AsyncAPIDefinition} hash={decodeURIComponent(hash)} containerRef={ref} lastModifiedDate={lastModifiedDate} isConsumerSide={isConsumerSide} />
    }

    if (activeRenderer === APIRenderers.ELEMENTS) {
      return (
        <div id="elements-renderer" className={clsx('pt-6', isConsumerSide && 'md:px-3 xl:px-0 md:pt-9 xl:pt-6')}>
          <LastModifiedDate date={lastModifiedDate} className="pb-[0.7rem] px-6" />
          <ElementsRenderer
            definition={definition as OpenAPI2Definition | OpenAPI3Definition}
            stacked={isAdmin}
            key={pathname}
          />
        </div>
      )
    }

    return <div className={clsx(isConsumerSide && 'md:p-3 xl:p-0')}>
      <SwaggerRenderer
        definition={definition as OpenAPI2Definition | OpenAPI3Definition}
        hash={decodeURIComponent(hash)}
        containerRef={ref}
        exploreUrl={exploreUrl}
        SHUBApiDetails={SHUBApiDetails}
        oasVersion={oasVersion}
        lastModifiedDate={lastModifiedDate}
      />
    </div>

  }, [renderer, definition, hash, ref, localRenderer, isAdmin])

  const onToggle = (value: boolean) => setLocalRenderer(value ? APIRenderers.SWAGGER_UI : APIRenderers.ELEMENTS)

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {
        isMobile &&
        <BreadcrumbBar key={`breadcrumb-${pathname}`} />
      }
      {shouldRenderToolbar && (
        <div data-testid="api-viewer-toolbar" className="flex top-0 justify-between w-full gap-1 px-6 md:px-9 xl:px-6 h-[80px] border-b border-b-neutral-30 dark:border-b-neutral-80 dark:bg-neutral-98">
          {!isMobile && <div className="flex items-center gap-2">
            {shouldRenderRouting ?
              <RoutingSettingsButton accentColor={accentColor} routingSettings={routingSettings} />
              : null
            }
          </div>}
          <div className="flex items-center gap-2">
            {shouldRenderOpenApiRendererToggle &&
              <div className={clsx(!isOas3 && 'mr-2')}>
                <OpenAPIRendererToggle onToggle={onToggle} renderer={localRenderer} />
              </div>}
            {
              !isMobile ?
                <>
                  {isOas3 ? <ExploreButton exploreUrl={exploreUrl} SHUBApiDetails={SHUBApiDetails} /> : null}
                  {shouldRenderCodeGen ?
                    <GenerateCodeDropdown
                      definition={definition as OpenAPI2Definition | OpenAPI3Definition}
                      generatorLanguages={generatorLanguages}
                      title={title}
                      productId={productId}
                      path={path}
                      accentColor={accentColor}
                    />
                    : null
                  }
                  {shouldRenderDownload ?
                    <Select
                      size="s"
                      aria-label={t('components.ui.apiViewer.downloadApi.ariaLabel')}
                      className="!w-[10.625rem] text-body2"
                      buttonClassName="text-neutral-70 dark:text-neutral-40"
                      optionsClassName="!w-[10.625rem] text-body2"
                      optionClassName="!whitespace-pre-wrap !break-all"
                      theme="neutral"
                      defaultValue={{ name: t('components.ui.apiViewer.downloadApi.label'), id: '' }}
                      options={apiDownloadOptions}
                      onChange={(option: SelectItem) => onDownloadApi(option.name as API_FILE_TYPES)}
                      isStatic={true}
                      accentColor={accentColor}
                    />
                    : null
                  }
                  {showEditButtons ?
                    <EditContentButtons
                      contentUrl={providerContentSlug}
                      shubUrl={shubUrl}
                    />
                    : null
                  }
                </> : null
            }
          </div>
        </div>
      )}

      <div className={clsx(
        'w-full overflow-auto dark:bg-neutral-98',
        shouldRenderToolbar ? 'h-[calc(100%_-_80px)]' : 'h-full',
        isConsumerSide && '2xl:max-w-[calc(1300px+3rem)] mx-auto')} ref={rendererRef}>
        {
          isConsumerSide && !isMobileScreen && <div className="px-5 pt-6">
            <BreadcrumbBar key={`breadcrumb-${pathname}`} />
          </div>
        }
        {renderApi()}
      </div>
    </div>
  )
}